import React, { useState } from "react";
import { Typography, Chip, Avatar } from "@mui/material";
import CustomTable from "../../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
// import AddClient from "../AddClient/AddClient";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useLoading } from "../../../contexts/LoadingContext";
import Modal from "../../../components/Modal/Modal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import BusinessIcon from "@mui/icons-material/Business";
import CircleIcon from "@mui/icons-material/Circle";
import FactoryIcon from "@mui/icons-material/Factory";
import PublicIcon from "@mui/icons-material/Public";
import { useAllProjectResorceMappingsViewModel } from "./AllProjectResourceMappingViewModel";
import AddProjectResourceMapping from "../AddProjectResourceMapping/AddProjectResourceMapping";
import { useRbac } from "../../../hooks/RbacContext";

const AllProjectResourceMappings = () => {
  const {
    projectResourceMappings,
    resources,
    projects,
    loading,
    error,
    getResourceName,
    getProjectName,
    createProjectResourceMapping,
    updateProjectResourceMapping,
    handleDelete,
    refetch,
    roles,
    getRoleName
  } = useAllProjectResorceMappingsViewModel();
  const { showLoading, hideLoading } = useLoading();

  const [isAddProjectResorceMappingOpen, setIsAddProjectResorceMappingOpen] = useState(false);
  const [selectedProjectResorceMapping, setSelectedProjectResorceMapping] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const { checkAccess } = useRbac();
  const columns = [
    {
      id: "project_id",
      label: "Project",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            color: (theme) =>
              value ? theme.palette.text.primary : theme.palette.text.secondary,
          }}
        >
          {getProjectName(value)}
        </Typography>
      ),
      searchValue: (value) => getProjectName(value),
    },
    {
      id: "resource_id",
      label: "Resource",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            color: (theme) =>
              value ? theme.palette.text.primary : theme.palette.text.secondary,
          }}
        >
          {getResourceName(value)}
        </Typography>
      ),
      searchValue: (value) => getResourceName(value),
    },
    {
      id: "role_id",
      label: "Role",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            color: (theme) =>
              value ? theme.palette.text.primary : theme.palette.text.secondary,
          }}
        >
          {getRoleName(value)}
        </Typography>
      ),
      searchValue: (value) => getRoleName(value),
    },
    {
      id: "start_date",
      label: "Start Date",
      align: "left",
    },
    {
      id: "end_date",
      label: "End Date",
      align: "left",
    },
    {
      id: "created_on",
      label: "Created On",
      align: "left",
    },

    {
      id: "updated_on",
      label: "Updated On",
      align: "left",
    }
  ];
  const fixedColumns = {
    left: [],
    right: ["is_active", "actions"],
  };
  const handleAddClient = () => {
    setSelectedProjectResorceMapping(null);
    setIsAddProjectResorceMappingOpen(true);
  };

  const handleEditClient = (client) => {
    setSelectedProjectResorceMapping(client);
    setIsAddProjectResorceMappingOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddProjectResorceMappingOpen(false);
    setSelectedProjectResorceMapping(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedProjectResorceMapping) {
        await updateProjectResourceMapping(selectedProjectResorceMapping, formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Project Resorce Mapping updated successfully",
        });
      } else {
        await createProjectResourceMapping(formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Project Resorce Mapping added successfully",
        });
      }
      handleCloseDialog();
      await refetch();
    } catch (error) {
      throw error;
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal(null);
  };

  const handleDeleteClick = (projectResorceMapping) => {
    setDeleteConfirmation({
      projectResorceMapping,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const projectResorceMapping = deleteConfirmation.projectResorceMapping;
    try {
      showLoading();
      await handleDelete(projectResorceMapping);
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Project Resorce Mapping deleted successfully",
      });
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete Project Resorce Mapping",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (client) => {

    const actions = [];
    if (checkAccess("projectsResourceMapping", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditClient,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    if (checkAccess("projectsResourceMapping", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: handleDeleteClick,
        color: "error",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
          },
        },
      });
    }

    return actions;
  };

  if (error) {
    return (
      <div>
        <Typography color="error">Error: {error}</Typography>
      </div>
    );
  }
  const columnFilters = [
    {
      id: "project_id",
      type: "select",
      label: "Project",
      render: (value) => getProjectName(value),
      icon: <BusinessIcon />,
      sendIcon: true,
    },
    {
      id: "resource_id",
      type: "select",
      label: "Resource",
      icon: <BusinessIcon />,
      render: (value) => getResourceName(value),
      sendIcon: true,
    },
    {
      id: "role_id",
      type: "select",
      label: "Role",
      icon: <BusinessIcon />,
      render: (value) => getRoleName(value),
      sendIcon: true,
    },
  ];

  return (
    <div>
      <CustomTable
        columns={columns}
        data={projectResourceMappings}
        onAdd={handleAddClient}
        showActions={true}
        showAddButton={checkAccess("projectsResourceMapping","add")}
        addButtonIcon={<PersonAddIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="All Project Resource Mapping"
        subtitle="Manage and monitor all Project Resource Mapping"
        addButtonText="Add Project Resource Mapping"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
      />

      <AddProjectResourceMapping
        open={isAddProjectResorceMappingOpen}
        onClose={handleCloseDialog}
        initialData={selectedProjectResorceMapping}
        onSubmit={handleSubmit}
        resources={resources}
        getResourceName={getResourceName}
        projects={projects}
        getProjectName={getProjectName}
        roles={roles}

      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={handleCloseSuccessModal}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Project Resorce Mapping"
          message={`Are you sure you want to delete Project Resorce Mapping for Resource "${getResourceName(deleteConfirmation.projectResorceMapping.resource_id)}" and project "${getProjectName(deleteConfirmation.projectResorceMapping.project_id)}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default AllProjectResourceMappings;
